import { Box, Stack, SxProps, Theme, Typography } from '@mui/material';

import { ROW_NAME_SX, VERIFIED_BADGE_OVERHANG } from './cardRowStyles';
// Импорт относительный, а не из индекса entity: в тестах индекс мокается
import { VerifiedBadge } from './VerifiedBadge';

interface IProps {
  firstName: string;
  lastName: string;
  isVerified: boolean;
  sx?: SxProps<Theme>;
}

/**
 * Колонка под галку. Ширина ровно `VERIFIED_BADGE_OVERHANG`: галка сидит в
 * контейнере нулевой ширины и выступает за него на эти 22px, поэтому во
 * флекс-ряду место под неё приходится объявлять самой колонкой. `flexShrink: 0` —
 * иначе эллипсис имени съедал бы галку вместо лишних букв.
 *
 * Центрирована по вертикали — имя всегда одна строка (см. `ListRowFrame`
 * JSDoc `IRowField`/`CardListRow`), центр строки и центр галки совпадают.
 */
const BADGE_SLOT_SX: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  flexShrink: 0,
  width: `${VERIFIED_BADGE_OVERHANG}px`
};

/**
 * Имя и фамилия актёра — ОДНА строка с эллипсисом, галка сразу за ней (18976,
 * Р1, третий заход).
 *
 * Оба предыдущих захода правили клэмп на 2 строки, а не убирали его: первый
 * держал галку инлайново в тексте с `overflow-clip-margin`-резервом (клип
 * протекал вниз, поверх полей под именем), второй вынес галку соседним
 * флекс-элементом, но клэмп остался — и у ФИО, дожимающего обе строки клэмпа
 * целиком, третья, срезанная клэмпом строка всё равно рисовалась поверх полей
 * «Возраст/Город/Тип проекта» (замер `getBoundingClientRect` в браузере).
 * Одна строка убирает целый класс дефектов по построению: нет второй строки —
 * нет и третьей, высота строки перестаёт зависеть от длины имени, а галка
 * всегда помещается рядом с обрезанным именем, а не гоняется за концом
 * текстового потока.
 *
 * Полное имя остаётся доступно через `title` — там, где текст обрезан
 * эллипсисом.
 *
 * Плитку (`CardGridTile`) это не касается: там под именем фото, а не поля,
 * вертикали хватает на 2–3 строки, и это отревьюено отдельно.
 * @param props - пропсы
 * @param props.firstName - имя
 * @param props.lastName - фамилия
 * @param props.isVerified - подтверждён ли профиль (системное фото)
 * @param props.sx - стили текста имени
 */
export const CardNameLine = ({
  firstName,
  lastName,
  isVerified,
  sx = ROW_NAME_SX
}: IProps) => {
  const fullName = `${firstName} ${lastName}`;

  return (
    <Stack direction='row' alignItems='center' sx={{ minWidth: 0 }}>
      <Typography variant='h7' title={fullName} sx={sx}>
        {fullName}
      </Typography>
      {isVerified && (
        <Box sx={BADGE_SLOT_SX}>
          <VerifiedBadge size={18} />
        </Box>
      )}
    </Stack>
  );
};
